import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Sparkles, Euro, TrendingUp, Users } from "lucide-react";

/**
 * AIUsageWidget — compact dashboard-widget dat AI-usage deze maand toont.
 * Wordt alleen geladen voor teammanager+ (level >= 60).
 * Endpoints: /api/ai-usage/stats
 */
const BUCKET_LABELS = {
  ai_agent_chat: "AI-Chat",
  ai_rapportages_genereer: "Rapportages",
  ai_voice_transcribe: "Voice-to-Text",
};

const BUCKET_COLORS = {
  ai_agent_chat: "#3b82f6",
  ai_rapportages_genereer: "#8b5cf6",
  ai_voice_transcribe: "#10b981",
};

export default function AIUsageWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ai-usage/stats");
        setStats(data);
      } catch (err) {
        // 403 = user heeft geen toegang → widget verbergen
        if (err.response?.status === 403) {
          setError("forbidden");
        } else {
          setError("error");
        }
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <Card className="p-5 bg-white border-border" data-testid="ai-usage-widget-loading">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 rounded w-1/3" />
          <div className="h-8 bg-slate-200 rounded w-1/2" />
          <div className="h-2 bg-slate-100 rounded" />
        </div>
      </Card>
    );
  }

  // Verberg widget als geen toegang
  if (error === "forbidden" || !stats) return null;

  const totaal = stats.totaal_requests || 0;
  const kosten = stats.totaal_kosten_eur_schatting || 0;
  const buckets = Object.entries(stats.per_bucket || {});
  const rollen = Object.entries(stats.per_rol || {});

  return (
    <Card className="p-5 bg-white border-border" data-testid="ai-usage-widget">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold">
            AI-gebruik deze maand
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <h3 className="font-heading text-3xl font-bold text-slate-900" data-testid="ai-usage-total">
              {totaal.toLocaleString("nl-NL")}
            </h3>
            <span className="text-xs text-slate-500">requests</span>
          </div>
          <div className="mt-1 flex items-center gap-1 text-sm text-slate-600">
            <Euro className="h-3.5 w-3.5" />
            <span data-testid="ai-usage-cost">~ € {kosten.toFixed(2)}</span>
            <span className="text-xs text-slate-400">geschat</span>
          </div>
        </div>
        <div className="h-10 w-10 rounded-lg bg-[hsl(var(--brand-blue))]/10 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-[hsl(var(--brand-blue))]" />
        </div>
      </div>

      {/* Stacked bar per bucket */}
      {totaal > 0 && (
        <>
          <div className="h-2.5 rounded-full overflow-hidden flex bg-slate-100" data-testid="ai-usage-bar">
            {buckets.map(([bucket, info]) => {
              const pct = (info.totaal / totaal) * 100;
              return (
                <div
                  key={bucket}
                  className="h-full transition-all"
                  style={{ width: `${pct}%`, background: BUCKET_COLORS[bucket] || "#94a3b8" }}
                  title={`${BUCKET_LABELS[bucket] || bucket}: ${info.totaal}`}
                />
              );
            })}
          </div>

          <div className="mt-3 space-y-1.5">
            {buckets.map(([bucket, info]) => {
              const pct = ((info.totaal / totaal) * 100).toFixed(1);
              return (
                <div key={bucket} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full"
                      style={{ background: BUCKET_COLORS[bucket] || "#94a3b8" }}
                    />
                    <span className="text-slate-700 font-medium">{BUCKET_LABELS[bucket] || bucket}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-500">
                    <span>{info.totaal}</span>
                    <span className="text-slate-400">{pct}%</span>
                    <span className="text-slate-500 font-mono">€ {info.kosten_eur.toFixed(2)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Top-3 rollen */}
          {rollen.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                <Users className="h-3 w-3" />
                Actiefste rollen
              </div>
              <div className="flex flex-wrap gap-1.5">
                {rollen
                  .sort((a, b) => b[1].totaal - a[1].totaal)
                  .slice(0, 4)
                  .map(([rol, info]) => (
                    <span
                      key={rol}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-xs text-slate-700"
                      data-testid={`ai-usage-rol-${rol}`}
                    >
                      <span className="font-medium">{rol}</span>
                      <span className="text-slate-500 font-mono">{info.totaal}</span>
                    </span>
                  ))}
              </div>
            </div>
          )}
        </>
      )}

      {totaal === 0 && (
        <p className="text-xs text-slate-500 text-center py-4">
          Nog geen AI-requests deze maand.
        </p>
      )}
    </Card>
  );
}
